import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cpu,
  Cloud,
  MessageSquare,
  Plane,
  Smartphone,
  Home,
  Globe,
  Heart,
  Calendar,
  MapPin,
  ExternalLink,
  BookOpen,
  Shield
} from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import TiltedCard from './ui/TiltedCard';
import StarBorder from './ui/StarBorder';
import Magnet from './ui/Magnet';

interface Project {
  title: string;
  subtitle: string;
  location: string;
  period?: string;
  url?: string;
  description: string;
  achievements: string[];
  icon: React.ReactNode;
  category: string;
}

const courseProjects: Project[] = [
  {
    title: "Autonomous Indoor Farming System",
    subtitle: "Graduation Project",
    location: "Sabanci University, Istanbul",
    period: "Feb 2024 - Jan 2025",
    description: "Designed a system to autonomously grow a plant without human intervention using Arduino Uno and NodeMCU.",
    achievements: [
      "Used external libraries to code an Arduino Uno and NodeMCU to control sensors and pumps to ensure effective plant growth.",
      "Calibrated pH and EC sensors to ensure accurate reading of water parameters",
      "Developed a webserver for viewing historical readings from the sensors in a graphical format",
      "Collaborated to construct a working deep water culture setup along with the sensors and pumps.",
      "Developed an affordable system ready for replication and use in real life environments."
    ],
    icon: <Cpu className="w-6 h-6" />,
    category: "IoT & Hardware"
  },
  {
    title: "Scalable Food Ordering System",
    subtitle: "Cloud Computing",
    location: "Sabanci University, Istanbul",
    period: "May 2024",
    description: "Collaborated in the deployment of a food ordering website on Google Cloud using Kubernetes.",
    achievements: [
      "Collaborated in the deployment of a food ordering website on Google Cloud using Kubernetes",
      "Implemented Google Cloud Functions to separately handle email notifications and order amount calculations",
      "Deployed a Compute Instance running MySQL to handle database queries."
    ],
    icon: <Cloud className="w-6 h-6" />,
    category: "Cloud Computing"
  },
  {
    title: "Chat Application",
    subtitle: "Computer Networks",
    location: "Sabanci University, Istanbul",
    period: "Dec 2023",
    description: "Implemented a chat application in C# Forms with custom channel subscription.",
    achievements: [
      "Implemented a chat application in C# Forms with custom channel subscription.",
      "Utilised socket programming with TCP to connect clients to a server that handles broadcasting of messages and handled user connections."
    ],
    icon: <MessageSquare className="w-6 h-6" />,
    category: "Networking"
  },
  {
    title: "Flight Roster Manager",
    subtitle: "Software Engineering",
    location: "Sabanci University, Istanbul",
    period: "Feb 2024 – May 2024",
    description: "Implemented APIs for a web-based flight roster system in Django.",
    achievements: [
      "Implemented APIs for a web-based flight roster system in Django.",
      "Developed a backend for the roster to obtain data from the APIs and present to the user at the frontend.",
      "Constructed methods in React to handle data retrieval and manipulation between the front and back end."
    ],
    icon: <Plane className="w-6 h-6" />,
    category: "Full Stack"
  },
  {
    title: "Recipe Application",
    subtitle: "Mobile Application Development",
    location: "Sabanci University, Istanbul",
    period: "Mar 2024 – May 2024",
    description: "Designed and implemented a food recipe application for android to view, store and delete personal food recipes.",
    achievements: [
      "Designed and implemented a food recipe application for android to view, store and delete personal food recipes.",
      "Utilised REST and Spring Framework to create endpoints responsible for the above-mentioned functions.",
      "Implemented a database in MongoDB Compass to store individual recipes."
    ],
    icon: <Smartphone className="w-6 h-6" />,
    category: "Mobile Development"
  },
  {
    title: "Smart Home Appliance Control System",
    subtitle: "Project Course",
    location: "Sabanci University, Istanbul",
    period: "Oct 2022 – Jan 2023",
    description: "Implemented a smart home system using ESP8266/NodeMCU to allow users control home electronics.",
    achievements: [
      "Implemented a smart home system using ESP8266/NodeMCU to allow users control home electronics.",
      "Utilised 3rd party libraries to code the NodeMCU to communicate between Blynk and the lights.",
      "Designed a system in Blynk to control the lights connected to the NodeMCU."
    ],
    icon: <Home className="w-6 h-6" />,
    category: "IoT & Hardware"
  }
];

const personalProjects: Project[] = [
  {
    title: "CyberGuard AI",
    subtitle: "Advanced Malware Detection System",
    location: "Lahore, Pakistan",
    description: "AI-powered cybersecurity application leveraging machine learning for malicious Java program detection through comprehensive bytecode analysis.",
    achievements: [
      "Built full-stack application with Django REST backend, React TypeScript frontend, and advanced ML pipeline achieving 96%+ accuracy.",
      "Developed sophisticated feature extraction engine analyzing 29 comprehensive bytecode features including API usage patterns and security indicators.",
      "Implemented Random Forest classifier outperforming neural network approaches with 100% ROC AUC score and superior interpretability for cybersecurity domain.",
      "Created secure file upload system with real-time scanning capabilities and detailed risk assessment reporting.",
      "Designed production-ready architecture with asynchronous processing, comprehensive testing suite, and Docker containerization support."
    ],
    icon: <Shield className="w-6 h-6" />,
    category: "Cybersecurity & AI"
  },
  {
    title: "Royal Taj Delivery Platform",
    subtitle: "Personal Project",
    location: "Istanbul, Turkey",
    url: "royaltajsu.pythonanywhere.com",
    description: "Implemented a web system for the students of Sabanci University to order food from Royal Taj in bulk.",
    achievements: [
      "Implemented a web system for the student of Sabanci University to order food from Royal Taj in bulk while ensuring a minimum delivery quota was met.",
      "Designed custom features to allow the restaurant to view orders, change daily quota and send order information to customers.",
      "Generated a monthly revenue of 30,000+ TL for the restaurant through the platform that brought in multiple orders from students daily."
    ],
    icon: <Globe className="w-6 h-6" />,
    category: "Web Development"
  },
  {
    title: "Patient Management System",
    subtitle: "Personal Project",
    location: "Lahore, Pakistan",
    description: "Engineered a patient management system in PHP and MySQL for a doctor.",
    achievements: [
      "Engineered a patient management system in PHP and MySQL for a doctor.",
      "Designed a custom DAS28 calculator for keeping record of patients with rheumatoid arthritis"
    ],
    icon: <Heart className="w-6 h-6" />,
    category: "Healthcare"
  },
  {
    title: "Library Management System",
    subtitle: "Personal Project",
    location: "Lahore, Pakistan",
    description: "Implemented a library management system in PHP and MySQL for the library at BCP Gulberg.",
    achievements: [
      "Implemented a library management system in PHP and MySQL for the library at BCP Gulberg.",
      "Designed database schema to efficiently manage book inventory, member records, and lending transactions.",
      "Created user-friendly interface for librarians to track book checkouts, returns, and overdue items."
    ],
    icon: <BookOpen className="w-6 h-6" />,
    category: "Database Management"
  }
];

const tabs = [
  { id: 'personal', label: 'Personal', projects: personalProjects },
  { id: 'course', label: 'University', projects: courseProjects },
] as const;

type TabId = typeof tabs[number]['id'];

const ProjectCard = ({ project, index }: { project: Project; index: number }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 40, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -20, scale: 0.97 }}
    transition={{ duration: 0.45, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
    className="h-full"
  >
    <TiltedCard rotateAmplitude={6} scaleOnHover={1.02} className="h-full">
      <div className="glass-effect rounded-2xl p-6 h-full transition-shadow duration-300 hover:shadow-xl hover:shadow-primary-500/10">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
            {project.icon}
          </div>
          <div className="flex-grow">
            <h3 className="text-xl font-bold text-white mb-1">{project.title}</h3>
            <p className="text-primary-400 font-medium mb-2">{project.subtitle}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <StarBorder color="#667eea" speed="5s">
                <span className="text-xs text-gray-300">{project.category}</span>
              </StarBorder>
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-gray-300 text-sm">
            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>{project.location}</span>
          </div>
          {project.period && (
            <div className="flex items-center text-gray-300 text-sm">
              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>{project.period}</span>
            </div>
          )}
          {project.url && (
            <Magnet padding={40} magnetStrength={5}>
              <div className="flex items-center text-primary-400 text-sm">
                <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
                <a href={`https://${project.url}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {project.url}
                </a>
              </div>
            </Magnet>
          )}
        </div>

        <p className="text-gray-300 text-sm mb-4 leading-relaxed">{project.description}</p>

        <div className="space-y-2">
          {project.achievements.slice(0, 3).map((achievement, achIndex) => (
            <div key={achIndex} className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-400 text-sm leading-relaxed">{achievement}</p>
            </div>
          ))}
        </div>
      </div>
    </TiltedCard>
  </motion.div>
);

const Projects: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('personal');
  const activeProjects = tabs.find((t) => t.id === activeTab)!.projects;

  return (
    <section id="projects" className="section-padding px-6 bg-gradient-to-b from-transparent to-dark-100/20">
      <div className="max-w-7xl mx-auto">
        <SectionHeading eyebrow="Things I've Built" title="Featured Projects" />

        {/* Tab switcher with sliding pill */}
        <div className="flex justify-center mb-12">
          <div className="flex gap-1 p-1.5 rounded-full glass-effect">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-300 ${
                  activeTab === tab.id ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="project-tab-pill"
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500"
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  />
                )}
                <span className="relative z-10">
                  {tab.label}
                  <span className="ml-2 text-xs opacity-70">{tab.projects.length}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {activeProjects.map((project, index) => (
              <ProjectCard key={project.title} project={project} index={index} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;
